import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Network, MapPin } from "lucide-react";
import { useLang } from "@/App";
import { Reveal, Overline } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-lined bg-white px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink/35 focus:border-teal focus:ring-2 focus:ring-teal/15";

function LicenseCard({ data, icon: Icon, testid, btnTestid, onSelect, cta, featured }) {
  return (
    <Reveal className="h-full" delay={featured ? 0.05 : 0.15}>
      <div
        data-testid={testid}
        className={`flex h-full flex-col rounded-3xl p-8 sm:p-10 ${
          featured
            ? "grain-overlay relative overflow-hidden bg-deep text-bone"
            : "border border-lined bg-white"
        }`}
      >
        {featured && (
          <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-aqua/15 blur-3xl" aria-hidden="true" />
        )}
        <div className="flex items-center justify-between">
          <span
            className={`font-mono text-[11px] font-semibold uppercase tracking-[0.22em] ${
              featured ? "text-aqua" : "text-teal"
            }`}
          >
            {data.badge}
          </span>
          <Icon className={`h-6 w-6 ${featured ? "text-aqua" : "text-teal"}`} />
        </div>
        <h3 className="mt-5 font-headline text-2xl font-bold tracking-tight">{data.title}</h3>
        <p className={`mt-3 text-sm leading-relaxed ${featured ? "text-bone/70" : "text-ink/65"}`}>{data.desc}</p>
        <ul className="mt-7 flex-1 space-y-3.5">
          {data.features.map((f, i) => (
            <li key={i} className={`flex items-start gap-3 text-sm ${featured ? "text-bone/85" : "text-ink/75"}`}>
              <Check className={`mt-0.5 h-4 w-4 shrink-0 ${featured ? "text-aqua" : "text-teal"}`} />
              {f}
            </li>
          ))}
        </ul>
        <button
          data-testid={btnTestid}
          onClick={onSelect}
          className={`mt-9 rounded-full px-6 py-3.5 font-headline text-sm font-bold transition-colors duration-300 ${
            featured
              ? "bg-aqua text-slate-950 hover:bg-white"
              : "border border-teal/30 text-teal hover:bg-teal hover:text-bone"
          }`}
        >
          {cta}
        </button>
      </div>
    </Reveal>
  );
}

export default function Licensing() {
  const { t } = useLang();
  const f = t.licensing.form;
  const [form, setForm] = useState({
    full_name: "",
    organization: "",
    email: "",
    phone: "",
    department: "",
    license_type: "",
    pond_count: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const chooseType = (type) => {
    setForm((prev) => ({ ...prev, license_type: type }));
    document.querySelector("#request-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/license-requests`, form);
      toast.success(f.success);
      setForm({ full_name: "", organization: "", email: "", phone: "", department: "", license_type: form.license_type, pond_count: "", message: "" });
    } catch (err) {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section className="bg-bone pb-16 pt-20 sm:pt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{t.licensing.hero.overline}</Overline>
            <h1 data-testid="licensing-hero-title" className="mt-4 max-w-3xl font-headline text-4xl font-extrabold leading-[1.08] tracking-tight text-deep sm:text-5xl">
              {t.licensing.hero.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg">
              {t.licensing.hero.sub}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-bone pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <LicenseCard
              data={t.licensing.coop}
              icon={Network}
              testid="licensing-coop-card"
              btnTestid="select-coop-license-btn"
              cta={t.licensing.selectBtn}
              featured
              onSelect={() => chooseType("cooperativa")}
            />
            <LicenseCard
              data={t.licensing.site}
              icon={MapPin}
              testid="licensing-site-card"
              btnTestid="select-site-license-btn"
              cta={t.licensing.selectBtn}
              onSelect={() => chooseType("sitio")}
            />
          </div>
        </div>
      </section>

      <section id="request-form" className="emerald-mesh grain-overlay relative overflow-hidden py-20 sm:py-28">
        <div className="hero-glow absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>Dekrbat Pure</Overline>
            <h2 className="mt-4 font-headline text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              {f.title}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-ink/70">{f.desc}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <form
              data-testid="license-form"
              onSubmit={submit}
              className="mt-10 grid grid-cols-1 gap-5 rounded-3xl border border-lined bg-white/85 p-7 shadow-xl shadow-sky-900/5 backdrop-blur-md sm:grid-cols-2 sm:p-10"
            >
              <div>
                <label htmlFor="lf-name" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/60">{f.name} *</label>
                <input data-testid="license-form-name-input" id="lf-name" required minLength={2} value={form.full_name} onChange={set("full_name")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="lf-org" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/60">{f.org} *</label>
                <input data-testid="license-form-org-input" id="lf-org" required minLength={2} value={form.organization} onChange={set("organization")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="lf-email" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/60">{f.email} *</label>
                <input data-testid="license-form-email-input" id="lf-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="lf-phone" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/60">{f.phone} *</label>
                <input data-testid="license-form-phone-input" id="lf-phone" required minLength={6} value={form.phone} onChange={set("phone")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="lf-dept" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/60">{f.department} *</label>
                <input data-testid="license-form-department-select" id="lf-dept" required minLength={2} value={form.department} onChange={set("department")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="lf-type" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/60">{f.type} *</label>
                <select data-testid="license-form-type-select" id="lf-type" required value={form.license_type} onChange={set("license_type")} className={inputCls}>
                  <option value="" disabled>{f.selectPlaceholder}</option>
                  <option value="cooperativa">{f.typeCoop}</option>
                  <option value="sitio">{f.typeSite}</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="lf-ponds" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/60">{f.ponds} *</label>
                <input data-testid="license-form-ponds-input" id="lf-ponds" required value={form.pond_count} onChange={set("pond_count")} className={inputCls} placeholder="e.g. 4" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="lf-message" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/60">{f.message}</label>
                <textarea data-testid="license-form-message-input" id="lf-message" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
              </div>
              <div className="sm:col-span-2">
                <button
                  data-testid="license-form-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="w-full rounded-full bg-teal px-8 py-4 font-headline text-sm font-bold text-white transition-colors duration-300 hover:bg-deep disabled:opacity-60"
                >
                  {sending ? f.sending : f.submit}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
